import { pickT } from "@repo/shared";
import {
  INSTRUMENT_SYNC_STALE_DAYS,
  type InstrumentSyncHealth,
  instrumentSyncLevel,
} from "@repo/shared/types";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { T, T_RECOMPUTE } from "./instrument-sync-status.i18n";

/** "sync": an instrument fetched from a provider; "recompute": a series derived from others. */
export type SyncHealthVariant = "sync" | "recompute";

function daysSince(date: string | null): number | null {
  if (!date) return null;
  return Math.floor((Date.now() - new Date(`${date}T00:00:00Z`).getTime()) / 86_400_000);
}

/**
 * Roll an instrument's sync health up to one traffic-light level:
 * red = sync disabled, orange = failing or stale data, green = healthy.
 */
export function summarizeSyncHealth(
  health: InstrumentSyncHealth,
  lang: string,
  variant: SyncHealthVariant = "sync",
): { level: "ok" | "warn" | "error"; reasons: string[] } {
  const t = variant === "recompute" ? pickT(T_RECOMPUTE, lang) : pickT(T, lang);
  // Level comes from the shared classifier so the UI and the server-side
  // activation rule always agree; the reasons below are UI-only labels.
  const level = instrumentSyncLevel(health, Date.now());
  if (level === "ok") return { level, reasons: [t.ok] };

  const reasons: string[] = [];
  if (health.disabledAt) {
    reasons.push(
      `${t.syncDisabled} (${health.failures} ${t.failures})${health.error ? ` — ${t.lastError}: ${health.error}` : ""}`,
    );
  } else if (health.failures > 0) {
    reasons.push(
      `${t.syncFailing} (${health.failures} ${t.failures})${health.error ? ` — ${t.lastError}: ${health.error}` : ""}`,
    );
  }
  const age = daysSince(health.lastUpdate);
  if (age !== null && age > INSTRUMENT_SYNC_STALE_DAYS) {
    reasons.push(`${t.stale} — ${t.lastUpdate}: ${health.lastUpdate} (${age}d)`);
  }
  return { level, reasons };
}

const DOT_COLOR = {
  ok: "bg-success",
  warn: "bg-warning",
  error: "bg-destructive",
  none: "bg-muted-foreground/40",
} as const;

/**
 * Small traffic-light dot; hover lists the concrete reason(s).
 *
 * `nullLabel` turns the no-health case into a neutral grey dot with that text
 * instead of rendering nothing — for a series that has never been computed,
 * where "no row yet" is a state worth showing rather than an absence.
 */
export function InstrumentSyncDot({
  health,
  lang,
  variant = "sync",
  nullLabel,
}: {
  health: InstrumentSyncHealth | null;
  lang: string;
  variant?: SyncHealthVariant;
  nullLabel?: string;
}) {
  if (!health && !nullLabel) return null;
  const { level, reasons } = health
    ? summarizeSyncHealth(health, lang, variant)
    : { level: "none" as const, reasons: [nullLabel as string] };
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${DOT_COLOR[level]}`} />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {reasons.map((r) => (
            <div key={r}>{r}</div>
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Warning/error callout for dialogs; renders nothing when healthy. */
export function InstrumentSyncAlert({
  health,
  lang,
  variant = "sync",
}: {
  health: InstrumentSyncHealth | null;
  lang: string;
  variant?: SyncHealthVariant;
}) {
  if (!health) return null;
  const { level, reasons } = summarizeSyncHealth(health, lang, variant);
  if (level === "ok") return null;
  return (
    <Alert variant={level === "error" ? "destructive" : "warning"}>
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        {reasons.map((r) => (
          <div key={r}>{r}</div>
        ))}
      </AlertDescription>
    </Alert>
  );
}
